import * as Sentry from '@sentry/react';
import axios, { type AxiosRequestConfig } from 'axios';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useConfig } from '../config';
import { useNetwork } from '../network';

interface ServerTimeProviderProps {
  children: React.ReactNode;
  enabled?: boolean;
}

interface ServerTimeContextValue {
  serverTime: Date | null;
  timeOffset: number;
  lastSyncAt: Date | null;
  isSyncing: boolean;
  syncNow: () => Promise<void>;

  /**
   * Whether an external provider is handling sync (e.g., HeartbeatProvider)
   */
  isExternalSyncActive: boolean;

  /**
   * Update server time from external source (e.g., heartbeat)
   * @param serverTimestamp - Server timestamp in milliseconds
   */
  updateServerTime: (serverTimestamp: number) => void;

  /**
   * Register/unregister external sync provider for example HeartbeatProvider
   * Returns a cleanup function
   */
  registerExternalSync: () => () => void;
}

const ServerTimeContext = createContext<ServerTimeContextValue | undefined>(undefined);

function ServerTimeProvider({ children, enabled = true }: Readonly<ServerTimeProviderProps>) {
  const { isOnline } = useNetwork();
  const { heartbeat } = useConfig();
  const { url, intervalMs } = heartbeat ?? {};

  const [timeOffset, setTimeOffset] = useState<number>(0);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [externalSyncCount, setExternalSyncCount] = useState<number>(0);

  const timerRef = useRef<number | null>(null);
  const lastServerTimeRef = useRef<number | null>(null);
  const lastClientTimeRef = useRef<number | null>(null);

  const isExternalSyncActive = externalSyncCount > 0;

  // Only auto-sync when no external provider is active
  const shouldAutoSync = useMemo(() => {
    return Boolean(enabled && isOnline && url && !isExternalSyncActive);
  }, [enabled, isOnline, url, isExternalSyncActive]);

  const serverTime = useMemo(() => {
    if (lastSyncAt === null) return null;
    return new Date(Date.now() + timeOffset);
  }, [timeOffset, lastSyncAt]);

  /**
   * Register an external sync provider (e.g., HeartbeatProvider)
   * Returns cleanup function to unregister
   */
  const registerExternalSync = useCallback(() => {
    setExternalSyncCount((count) => count + 1);

    // Return cleanup function
    return () => {
      setExternalSyncCount((count) => count - 1);
    };
  }, []);

  const updateServerTime = useCallback((serverTimestamp: number) => {
    const clientTime = Date.now();
    const offset = serverTimestamp - clientTime;

    setTimeOffset(offset);
    setLastSyncAt(new Date());
    lastServerTimeRef.current = serverTimestamp;
    lastClientTimeRef.current = clientTime;
  }, []);

  const syncNow = useCallback(async () => {
    if (!url) return;

    try {
      setIsSyncing(true);
      const config: AxiosRequestConfig = {
        url,
        method: 'post',
        withCredentials: true,
      };

      const response = await axios(config);

      const serverTimestamp = response.data?.currentTime;

      if (serverTimestamp) {
        updateServerTime(serverTimestamp);
      }
    } catch (err) {
      console.error('Server time sync failed:', err);
      Sentry.captureException(err, {
        tags: {
          feature: 'server-time-sync',
        },
      });
    } finally {
      setIsSyncing(false);
    }
  }, [url, updateServerTime]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!shouldAutoSync) {
      clearTimer();
      return;
    }

    syncNow();

    timerRef.current = window.setInterval(syncNow, intervalMs || 30000);

    return () => {
      clearTimer();
    };
  }, [shouldAutoSync, intervalMs, syncNow, clearTimer]);

  const contextValue: ServerTimeContextValue = useMemo(
    () => ({
      serverTime,
      timeOffset,
      lastSyncAt,
      isSyncing,
      isExternalSyncActive,
      updateServerTime,
      syncNow,
      registerExternalSync,
    }),
    [
      serverTime,
      timeOffset,
      lastSyncAt,
      isSyncing,
      isExternalSyncActive,
      updateServerTime,
      syncNow,
      registerExternalSync,
    ],
  );

  return <ServerTimeContext.Provider value={contextValue}>{children}</ServerTimeContext.Provider>;
}

function useServerTime() {
  const ctx = useContext(ServerTimeContext);
  if (ctx === undefined) {
    throw new Error('useServerTime must be used within ServerTimeProvider');
  }
  return ctx;
}

export { ServerTimeProvider, ServerTimeContext, useServerTime };
export type { ServerTimeProviderProps, ServerTimeContextValue };
